(function (root, factory) {
  const api = factory(root, typeof module === 'object' && module.exports ? require('./dim-context-continuation.js') : root.DIMContextContinuation);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.DIMGuidedHandoff = api;
}(typeof globalThis !== 'undefined' ? globalThis : undefined, function (root, continuation) {
  'use strict';

  const HANDOFF_KEYS = ['schemaVersion', 'draftRevision', 'origin', 'createdAt', 'expiresAt', 'context', 'selection', 'creativeBrief', 'runtime', 'textSchemaVersion', 'textRendererVersion', 'textLayers'];
  const RUNTIME_KEYS = ['environment', 'instanceId', 'themeId', 'workerOrigin'];
  const clone = (value) => JSON.parse(JSON.stringify(value));
  const fail = (code) => { throw new Error(code); };
  const exactKeys = (value, keys, code) => {
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length !== keys.length || keys.some((key) => !Object.hasOwn(value, key))) fail(code);
    return value;
  };
  const exactKeysWithOptional = (value, keys, optional, code) => {
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).some((key) => !keys.includes(key) && ![].concat(optional).includes(key))
      || keys.some((key) => !Object.hasOwn(value, key))) fail(code);
    return value;
  };
  const text = (value, maximum, code) => {
    if (typeof value !== 'string' || !value.trim() || value.length > maximum) fail(code);
    return value;
  };
  const origin = (value, code) => {
    try { const parsed = new URL(value); if (parsed.origin !== value || parsed.protocol !== 'https:' || parsed.username || parsed.password) fail(code); return value; } catch (_) { fail(code); }
  };
  const context = (value) => {
    if (!continuation) fail('INVALID_CONTEXT');
    continuation.validateContext(value);
    return value;
  };
  const runtime = (value) => {
    exactKeys(value, RUNTIME_KEYS, 'INVALID_RUNTIME');
    if (!['development', 'production'].includes(value.environment)) fail('INVALID_RUNTIME');
    text(value.instanceId, 100, 'INVALID_RUNTIME');
    if (!/^[a-zA-Z0-9_.-]+$/.test(value.instanceId) || !/^[1-9][0-9]{0,29}$/.test(value.themeId)) fail('INVALID_RUNTIME');
    origin(value.workerOrigin, 'INVALID_RUNTIME');
    return value;
  };
  const sameContext = (left, right) => continuation.sameAuthorityIdentity(left, right);
  const selection = (value) => {
    exactKeys(value, ['productId', 'variantId', 'options'], 'INVALID_SELECTION');
    text(value.productId, 30, 'INVALID_SELECTION'); text(value.variantId, 30, 'INVALID_SELECTION');
    if (!Array.isArray(value.options) || value.options.length > 8) fail('INVALID_SELECTION');
    const names = new Set();
    for (const option of value.options) {
      exactKeys(option, ['name', 'value'], 'INVALID_SELECTION'); text(option.name, 80, 'INVALID_SELECTION'); text(option.value, 180, 'INVALID_SELECTION');
      if (names.has(option.name)) fail('INVALID_SELECTION');
      names.add(option.name);
    }
    return value;
  };
  const letteringResolution = (value) => {
    if (value === undefined) return null;
    exactKeys(value, ['choice', 'originalSubject', 'originalComposition', 'subject', 'compositionIntent', 'confirmed'], 'INVALID_LETTERING_RESOLUTION');
    if (value.choice !== 'edited-illustration' || value.confirmed !== true) fail('INVALID_LETTERING_RESOLUTION');
    for (const key of ['originalSubject', 'originalComposition', 'subject', 'compositionIntent']) text(value[key], 1000, 'INVALID_LETTERING_RESOLUTION');
    if (value.originalSubject === value.subject && value.originalComposition === value.compositionIntent) fail('INVALID_LETTERING_RESOLUTION');
    return value;
  };
  const design = (value, textModel) => {
    exactKeys(value, ['creativeBrief', 'textSchemaVersion', 'textRendererVersion', 'textLayers'], 'INVALID_DESIGN');
    text(value.creativeBrief, 4000, 'INVALID_DESIGN');
    if (value.textSchemaVersion !== 1 || value.textRendererVersion !== 1 || !textModel || typeof textModel.textState !== 'function' || typeof textModel.validateLayers !== 'function') fail('INVALID_DESIGN');
    const layers = textModel.textState(value);
    return { ...value, textLayers: textModel.validateLayers(layers, { forExport: true }) };
  };
  const modelFor = (options) => options?.textModel || root?.DIMTextModel;
  const action = (value) => {
    if (value === undefined) return null;
    exactKeys(value, ['id', 'kind'], 'INVALID_ACTION');
    if (typeof value.id !== 'string' || !/^[A-Za-z0-9_-]{8,120}$/.test(value.id) || !['generate', 'upload'].includes(value.kind)) fail('INVALID_ACTION');
    return value;
  };
  const scope = (value) => JSON.stringify([value.environment, value.instanceId, value.environment === 'development' ? value.themeId : null, value.workerOrigin]);

  function create(input, options = {}) {
    if (!input || !Number.isSafeInteger(input.draftRevision) || input.draftRevision < 0 || !Number.isSafeInteger(input.nowMs)) fail('INVALID_HANDOFF');
    const trustedContext = context(input.context); const trustedRuntime = runtime(input.runtime); const trustedSelection = selection(input.selection);
    const trustedDesign = design(input.design, modelFor(options)); const trustedOrigin = origin(input.origin, 'INVALID_HANDOFF');
    const trustedResolution = letteringResolution(input.letteringResolution);
    const trustedAction = action(input.action);
    return Object.freeze({ schemaVersion: 'dream-handoff@2', draftRevision: input.draftRevision, origin: trustedOrigin, createdAt: input.nowMs, expiresAt: input.nowMs + 7_200_000,
      context: clone(trustedContext), selection: clone(trustedSelection), creativeBrief: trustedDesign.creativeBrief, runtime: clone(trustedRuntime),
      textSchemaVersion: trustedDesign.textSchemaVersion, textRendererVersion: trustedDesign.textRendererVersion, textLayers: clone(trustedDesign.textLayers),
      ...(trustedResolution ? { letteringResolution: clone(trustedResolution) } : {}),
      ...(trustedAction ? { action: clone(trustedAction) } : {}) });
  }

  function validate(value, expected, isSelectionAllowed, options = {}) {
    exactKeysWithOptional(value, HANDOFF_KEYS, ['letteringResolution', 'action'], 'STALE_HANDOFF');
    if (!expected || !Number.isSafeInteger(expected.nowMs) || !Number.isSafeInteger(expected.draftRevision) || typeof isSelectionAllowed !== 'function') fail('STALE_HANDOFF');
    const checkedContext = context(value.context); const checkedRuntime = runtime(value.runtime); const checkedSelection = selection(value.selection);
    design({ creativeBrief: value.creativeBrief, textSchemaVersion: value.textSchemaVersion, textRendererVersion: value.textRendererVersion, textLayers: value.textLayers }, modelFor(options));
    letteringResolution(value.letteringResolution);
    action(value.action);
    if (value.schemaVersion !== 'dream-handoff@2' || value.draftRevision !== expected.draftRevision || value.origin !== expected.origin || !Number.isSafeInteger(value.createdAt)
      || !Number.isSafeInteger(value.expiresAt) || value.createdAt > expected.nowMs || value.expiresAt <= expected.nowMs || value.expiresAt <= value.createdAt || value.expiresAt - value.createdAt > 7_200_000
      || !sameContext(checkedContext, context(expected.context)) || scope(checkedRuntime) !== scope(runtime(expected.runtime)) || isSelectionAllowed(checkedSelection) !== true) fail('STALE_HANDOFF');
    return clone(value);
  }

  return Object.freeze({ create, validate, validateContext: context, validateRuntime: runtime, scope });
}));
